import { useCallback, useContext } from "react";
import { withApollo } from "react-apollo";
import { APPROVE_BOOKING } from "../../gql/bookingapi/mutations";
import { useSelector } from "react-redux";
import { OwcIcon } from "@one/react";
import { BookingHazardousChip } from "../../components/shared/BookingHazardousChip";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";

const HazardousApprovalActions = ({
  client,
  booking,
  disabled = false,
  setOpenHazardousRejectDialog,
  setHazardousBookingToReject,
  setOpenAssistanceDialog,
  setBookingForAssistance,
  setIsEditMode,
}) => {
  const { dispatchAction } = useContext(PaginationContext);
  const user = useSelector((store) => store.user);

  const bookingStatus = booking?.approval?.status?.toString().toUpperCase();
  const isPending = bookingStatus === "PENDING";

  const hasAdminRole = user?.roles?.includes("BookingAdmin") || false;
  const isAdmin = hasAdminRole

  // Check if booking requires assistance
  const requiresAssistance = booking?.assistanceDeclaration?.requiresAssistance === true;

  const handleApprove = useCallback(async () => {
    if (!booking?.id || !isPending) return;

    // If the booking requires assistance, open the assignment dialog
    if (requiresAssistance) {
      // Only open dialog if both setters are available
      if (setBookingForAssistance && setOpenAssistanceDialog && setIsEditMode) {
        setBookingForAssistance(booking);
        setIsEditMode(false); // Ensure we're in approval mode, not edit mode
        setOpenAssistanceDialog(true);
      }
      return;
    }

    // Otherwise, proceed with direct approval
    try {
      // Don't set individual status - page-level loading handles this
      dispatchAction({
        type: "fetching",
        payload: true,
      });

      await client.mutate({
        mutation: APPROVE_BOOKING,
        variables: {
          bookingId: booking.id,
          approval: {
            status: "APPROVED",
            approvedBy: user.user,
            approvedByEmail: user.email,
          },
          createdBy: user.user,
          createdByEmail: user.email,
        },
      });

      // Don't handle completion here - let the subscription callback handle it
    } catch (error) {
      console.error("Approval error:", error);
      dispatchAction({
        type: "clearAll",
      });
    }
  }, [booking, client, user, isPending, dispatchAction, requiresAssistance, setBookingForAssistance, setOpenAssistanceDialog, setIsEditMode]);

  const handleRejectClick = useCallback(() => {
    if (setHazardousBookingToReject) {
      setHazardousBookingToReject(booking);
    }
    if (setOpenHazardousRejectDialog) {
      setOpenHazardousRejectDialog(true);
    }
  }, [booking, setHazardousBookingToReject, setOpenHazardousRejectDialog]);

  return (
    <>
      {/* Show chip for non-admin users or non-pending bookings */}
      {(!isAdmin || !isPending) && (
        <div style={{ marginBottom: "8px" }}>
          <BookingHazardousChip item={booking} />
        </div>
      )}

      {/* Action Buttons - Only for admin users with pending hazardous bookings */}
      {isPending && isAdmin && (
        <div style={{ display: "flex", gap: "1rem" }}>
          <button
            style={{
              background: "transparent",
              border: "none",
              padding: "0",
              color: "#CC0033",
              display: "flex",
              alignItems: "center",
              gap: ".25rem",
              cursor: disabled ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "500",
              fontFamily: "inherit",
              opacity: disabled ? 0.6 : 1,
            }}
            onClick={handleRejectClick}
            disabled={disabled}
            data-testid="hazardous-reject-link"
          >
            <OwcIcon name="clear" style={{ color: "#CC0033", cursor: "pointer" }} />
            Reject
          </button>

          <button
            style={{
              background: "transparent",
              border: "none",
              padding: "0",
              color: "#0B41CD",
              display: "flex",
              alignItems: "center",
              gap: ".25rem",
              cursor: disabled ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "500",
              fontFamily: "inherit",
              opacity: disabled ? 0.6 : 1,
            }}
            onClick={handleApprove}
            disabled={disabled}
            data-testid="hazardous-approve-link"
          >
            <OwcIcon name="confirm" style={{ color: "#0B41CD", cursor: "pointer" }} />
            Approve
          </button>

        </div>
      )}
    </>
  );
};

export default withApollo(HazardousApprovalActions);
